import {distinct, useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../../api/API";
import {useGetAll} from "../../../../logistics/routes/useGetAll";
import {AsyncHelper} from "../AsyncHelper";
import {LogisticsLogItem, LogisticsSummary} from "../../../../../api/Logistics";
import {LogisticsSummaryItem} from "./LogisticsSummaryItem";
import {Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import moment from "moment";
import {Link} from "react-router-dom";
import {deliveryUrl, poUrl, routeUrl} from "./links";
import {useMemo, useState} from "react";
import {JournalEntry} from "../../../../../api/ProjectJournal";
import {DeliveryProofPopup} from "../../../manage/journal/rows/DeliveryProofPopup";
import {css} from "@emotion/css";
import {useProjectId} from "../../ProjectName";

export function Logistics(props: {
    opening?: number;
    miscProduct?: number;
    inventoryRequest?: number;
}) {

    const summaryReq = useAsync2((input) => api.logistics.logGetSummary(input),
        {
            opening: props.opening,
            miscProduct: props.miscProduct,
            inventoryRequest: props.inventoryRequest,
        },
        [props.opening, props.miscProduct, props.inventoryRequest]);

    const listItemReq = useGetAll((input) => api.logistics.logGetItems(input),
        {
            opening: props.opening,
            miscProduct: props.miscProduct,
            inventoryRequest: props.inventoryRequest,
        },
        [props.opening, props.miscProduct, props.inventoryRequest], {
            useLazy: true,
        });

    const summary = summaryReq.result;
    const deliveries = useMemo(() => {
        if(!summary) return "";
        return distinct(summary.map(s => s.delivery?.id).filter(s => !!s)).join(",");
    }, [summary]);

    const journalReq = useGetAll(async (input) => {
        if(!input.deliveries) return { data: [], count: 0 };
        return api.projectJournal.list(input)
    }, {
        deliveries: deliveries,
    }, [deliveries], {
        useLazy: true,
    });

    const journalRs = journalReq.result;
    const deliveryProofs = useMemo(() => {
        if(!journalRs) return {};
        const map: {[key: number]: JournalEntry} = {};
        journalRs.forEach(j => {
            if(j.type === "delivery-complete") {
                map[j.delivery] = j;
            }
        });

        return map;
    }, [journalRs]);

    const [showJournal, setShowJournal] = useState<JournalEntry | null>(null);
    const projectId = useProjectId();

    return (
        <div>
            {showJournal && <DeliveryProofPopup
                value={showJournal}
                onClose={() => setShowJournal(null)}
            />}
            <AsyncHelper<LogisticsSummary[]> {...summaryReq}>
                {sum => <>
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                    }}>
                        {sum.map((s, index) => {
                            if(s.po) {
                                return (
                                    <LogisticsSummaryItem key={index.toString()}
                                                          title={"PO #" + s.po.poNumber}
                                                          subTitle={moment(s.lastUpdated).format("MMM D, YYYY [at] ha")}
                                                          status={s.po.status}
                                                          link={poUrl(s.po.id)} />
                                )
                            }

                            if(s.route) {
                                return (
                                    <LogisticsSummaryItem
                                        key={index.toString()}
                                          title={"Route " + s.route.name}
                                          subTitle={moment(s.lastUpdated).format("MMM D, YYYY [at] ha")}
                                          status={s.route.completed ? "completed" : "pending"}
                                          link={routeUrl(s.route.id)} />
                                )
                            }

                            if(s.delivery) {
                                return (
                                    <LogisticsSummaryItem
                                        key={index.toString()}
                                          title={s.delivery.projectDeliveryNum ? `Delivery #${s.delivery.projectDeliveryNum} (Packing ${s.delivery.name})` : "Packing " + s.delivery.name}
                                          subTitle={moment(s.lastUpdated).format("MMM D, YYYY [at] ha")}
                                          status={s.delivery.status}
                                          link={deliveryUrl(s.delivery.id)} />
                                )
                            }

                            if(s.freeformChange) {
                                return (
                                    <LogisticsSummaryItem
                                        key={index.toString()}
                                          title={"Change #" + s.freeformChange.id}
                                          subTitle={moment(s.lastUpdated).format("MMM D, YYYY [at] ha")}
                                          status={s.freeformChange.status}
                                          link={`/project/${projectId}/shop-drawing/changes/ch/${s.freeformChange.id}`} />
                                )
                            }

                            return null;
                        })}
                    </div>
                </>}
            </AsyncHelper>

            {journalReq.error ? journalReq.LoadingOrErrorElement : null}

            <AsyncHelper<LogisticsLogItem[]> {...listItemReq}>
                {list => <>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ref</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Detail</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map(l => {
                                const journalEntry = l.delivery && l.description.indexOf("Delivered") !== -1 ? deliveryProofs[l.delivery] : null;

                                return (<TableRow key={l.id}>
                                    <TableCell style={{textAlign: "right"}}>{l.id}</TableCell>
                                    <TableCell>{moment(l.createdAt).format("MMM D [at] h:mma")}</TableCell>
                                    <TableCell>{l.createdByName}</TableCell>
                                    <TableCell>
                                        {l.description}
                                    </TableCell>
                                    <TableCell>
                                        {l.route ? <Link className={linkCls} to={routeUrl(l.route)}>Route #{l.route}</Link> :
                                            l.delivery ? <Link className={linkCls} to={deliveryUrl(l.delivery)}>Pack #{l.delivery}</Link> :
                                                l.purchaseOrder ? <Link className={linkCls} to={poUrl(l.purchaseOrder)}>PO #{l.poNumber}</Link> :
                                                    null
                                        }
                                        {journalEntry && <a className={linkCls} style={{textDecoration: "underline", color: "initial", cursor: "pointer"}}
                                                            onClick={() => setShowJournal(journalEntry)}>
                                            View Proof
                                        </a>}
                                    </TableCell>
                                </TableRow>)
                            })}
                            {list.length === 0 && <TableRow key="no-data">
                                <TableCell colSpan={5}>
                                    No History
                                </TableCell>
                            </TableRow>}
                        </TableBody>
                    </Table>
                </>}
            </AsyncHelper>
        </div>
    )
}

const linkCls = css({
    display: "block",
    whiteSpace: "nowrap"
})